
import { supabase } from '@/integrations/supabase/client';

// Define interface for the settings data structure with proper typing
interface SettingsData {
  company_name?: string;
  email?: string;
  phone?: string;
  whatsapp_link?: string;
  welcome_text?: string;
  footer_text?: string;
  logo_url?: string | null;
  [key: string]: any; // Allow other properties
}

// Define interface for the payload structure
interface SettingsPayload {
  new: SettingsData;
  old?: SettingsData;
  eventType?: string;
  [key: string]: any; // Allow other properties
}

// Cache for realtime data to reduce redundant updates
const realtimeCache = {
  settings: {
    lastUpdate: 0,
    data: null as SettingsData | null
  }
};

// Singleton pattern pour garantir une seule instance de canal realtime
let activeChannel: any = null;

/**
 * Configure et active les canaux realtime Supabase pour écouter les changements de tables
 * @returns Le canal Supabase configuré ou null en cas d'erreur
 */
export const enableRealtimeForTables = async () => {
  // Si un canal existe déjà, le retourner
  if (activeChannel) {
    return activeChannel;
  }
  
  try {
    // Configuration du canal realtime pour les tables settings seulement
    const channel = supabase
      .channel('app-realtime')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public',
          table: 'settings'
        },
        (payload: SettingsPayload) => {
          // Récupérer les données complètes du payload avec typage
          const newData = payload.new;
          const now = Date.now();
          
          // Throttle updates to prevent redundant updates (minimum 2 secondes)
          if (now - realtimeCache.settings.lastUpdate < 2000) {
            console.log('🔄 Throttled settings update');
            return;
          }
          
          // Vérifier si les données sont disponibles avant de les traiter
          if (newData) {
            // Update cache
            realtimeCache.settings.lastUpdate = now;
            realtimeCache.settings.data = newData;
            
            // Convertir les noms de colonnes pour correspondre à notre format frontend
            const formattedData = {
              title: newData.title || '',
              email: newData.email || '',
              phone: newData.phone || '',
              whatsappLink: newData.whatsapp_link || '',
              
              footerText: newData.footer_text || '',
              logoUrl: newData.logo_url || null,
              showVirtualAssistant: newData.show_virtual_assistant ?? true,
            };
            
            // Diffuser un évènement global pour tous les composants avec les données complètes
            window.dispatchEvent(new CustomEvent('settings-changed', { 
              detail: { 
                source: 'realtime',
                data: formattedData, 
                timestamp: now 
              }
            }));
          }
        }
      )
      .subscribe((status) => {
        // Forcer une mise à jour initiale lors de la connexion au canal
        if (status === 'SUBSCRIBED') {
          console.log('Canal realtime connecté avec succès');
        }
      });

    activeChannel = channel;
    return channel;
  } catch (error) {
    console.error('⚠️ Erreur lors de la configuration realtime:', error);
    return null;
  }
};

// Helper to invalidate cache and force refresh
export const invalidateRealtimeCache = () => {
  realtimeCache.settings.lastUpdate = 0;
  realtimeCache.settings.data = null;
};
